<template>
  <div class="scan-page">
    <header class="scan-header">
      <h1>Check-in</h1>
      <p class="sub">
        <span v-if="authStore.adminName">{{ authStore.adminName }} · </span>
        <span v-if="eventoCheckin">{{ eventoCheckin.nome }}</span>
        <span v-else-if="authStore.isCheckin">Sem evento vinculado</span>
        <span v-else>Voluntário</span>
      </p>
    </header>

    <div v-if="!authStore.isAuthenticated" class="aviso aviso-warn">
      Faça login no painel pra registrar check-ins.
      <router-link to="/admin/login" class="link">Entrar</router-link>
    </div>

    <div v-else-if="authStore.isCheckin && !authStore.admin?.eventoCheckinId" class="aviso aviso-warn">
      Você ainda não está vinculado a nenhum evento. Peça pra um administrador te vincular.
    </div>

    <section v-else class="scan-section">
      <div class="scanner-card card">
        <div class="scanner-mode-tabs">
          <button :class="['mode-tab', { active: mode === 'camera' }]" @click="switchMode('camera')">
            📷 Câmera
          </button>
          <button :class="['mode-tab', { active: mode === 'manual' }]" @click="switchMode('manual')">
            ⌨️ Manual
          </button>
        </div>

        <div v-if="mode === 'camera'" class="camera-area">
          <div id="qr-reader" ref="qrReader"></div>
          <div v-if="cameraError" class="aviso aviso-warn">{{ cameraError }}</div>
        </div>

        <div v-else class="manual-area">
          <label class="label">Cole o código da inscrição</label>
          <input class="input" v-model="manualCode" placeholder="cuid..." @keydown.enter="processarManual" />
          <button class="btn-primary btn-block" @click="processarManual" :disabled="!manualCode">
            Buscar
          </button>
        </div>
      </div>

      <div v-if="processing" class="aviso aviso-info">Buscando...</div>
    </section>

    <!-- Modal de confirmação -->
    <div v-if="dryRunResult" class="modal-overlay" @click.self="fechaModal">
      <div class="modal-card confirm-card">
        <div :class="['conf-icon', dryRunResult.alreadyCheckedIn ? 'icon-info' : 'icon-ok']">
          {{ dryRunResult.alreadyCheckedIn ? 'ℹ️' : '✅' }}
        </div>
        <h2 v-if="dryRunResult.alreadyCheckedIn">Já confirmado</h2>
        <h2 v-else>Confirmar check-in?</h2>
        <p class="participante">{{ dryRunResult.inscricao.participanteNome }}</p>
        <p class="evento-info">
          {{ dryRunResult.inscricao.evento?.nome }}<br>
          <small>{{ dryRunResult.inscricao.plano?.nome }}</small>
        </p>
        <div v-if="dryRunResult.alreadyCheckedIn" class="ja-info">
          Confirmado em {{ formatDateTime(dryRunResult.inscricao.checkInAt) }}
          <span v-if="dryRunResult.inscricao.checkInBy">
            por {{ dryRunResult.inscricao.checkInBy.name }}
          </span>
        </div>
        <div class="modal-actions">
          <button class="btn-secondary" @click="fechaModal">{{ dryRunResult.alreadyCheckedIn ? 'Ok' : 'Cancelar' }}</button>
          <button v-if="!dryRunResult.alreadyCheckedIn" class="btn-primary" @click="confirmar" :disabled="confirming">
            {{ confirming ? 'Confirmando...' : 'Confirmar' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Toast de sucesso -->
    <transition name="toast">
      <div v-if="ultimaConfirmacao" class="success-bar">
        ✅ <strong>{{ ultimaConfirmacao }}</strong> confirmado!
      </div>
    </transition>
  </div>
</template>

<script>
import { useAuthStore } from '../../stores/auth'
import { scanInscricao } from '../../lib/checkin'
import api from '../../lib/api'

let html5QrCode = null

export default {
  name: 'ScanPage',
  data() {
    return {
      mode: 'camera',
      manualCode: '',
      processing: false,
      dryRunResult: null,
      confirming: false,
      cameraError: '',
      ultimaConfirmacao: '',
      eventoCheckin: null
    }
  },
  computed: {
    authStore() { return useAuthStore() }
  },
  async mounted() {
    if (this.authStore.isAuthenticated) {
      await this.authStore.refresh()
      if (this.authStore.admin?.eventoCheckinId) {
        try {
          const { data } = await api.get(`/eventos/${this.authStore.admin.eventoCheckinId}`)
          this.eventoCheckin = data.evento
        } catch {}
      }
      if (this.canScan()) {
        await this.startCamera()
      }
    }
  },
  beforeUnmount() { this.stopCamera() },
  methods: {
    canScan() {
      if (this.authStore.isCheckin) return !!this.authStore.admin?.eventoCheckinId
      return this.authStore.isRoot || this.authStore.isAdmin
    },
    formatDateTime(d) { try { return new Date(d).toLocaleString('pt-BR') } catch { return d } },
    async switchMode(m) {
      if (this.mode === m) return
      this.mode = m
      if (m === 'camera') {
        await this.$nextTick()
        await this.startCamera()
      } else {
        this.stopCamera()
      }
    },
    async startCamera() {
      try {
        this.cameraError = ''
        const mod = await import('html5-qrcode')
        const Html5Qrcode = mod.Html5Qrcode
        html5QrCode = new Html5Qrcode('qr-reader')
        await html5QrCode.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 240, height: 240 } },
          this.onScanSuccess,
          () => {}
        )
      } catch (err) {
        this.cameraError = 'Não foi possível acessar a câmera. Use o modo manual.'
      }
    },
    stopCamera() {
      if (html5QrCode) {
        try { html5QrCode.stop().catch(() => {}) } catch {}
        try { html5QrCode.clear() } catch {}
        html5QrCode = null
      }
    },
    extractId(text) {
      if (!text) return null
      const m = text.match(/[a-z0-9]{24,30}/i)
      return m ? m[0] : text.trim()
    },
    async onScanSuccess(decodedText) {
      if (this.processing || this.dryRunResult) return
      const id = this.extractId(decodedText)
      if (!id) return
      await this.lookup(id)
    },
    async processarManual() {
      if (!this.manualCode) return
      const id = this.extractId(this.manualCode)
      await this.lookup(id)
    },
    async lookup(id) {
      this.processing = true
      try {
        const res = await scanInscricao(id, { dryRun: true })
        this.dryRunResult = res
        this.stopCamera()
      } catch (err) {
        const msg = err.response?.data?.message || 'Inscrição inválida ou não encontrada'
        this.$toast.open({ message: msg, type: 'error', position: 'top-right', duration: 5000 })
      } finally {
        this.processing = false
      }
    },
    async confirmar() {
      if (!this.dryRunResult) return
      this.confirming = true
      try {
        const res = await scanInscricao(this.dryRunResult.inscricao.id, { dryRun: false })
        this.ultimaConfirmacao = res.inscricao.participanteNome
        setTimeout(() => { this.ultimaConfirmacao = '' }, 3500)
        this.fechaModal()
      } catch (err) {
        this.$toast.open({ message: err.response?.data?.message || 'Falha ao confirmar', type: 'error', position: 'top-right' })
      } finally {
        this.confirming = false
      }
    },
    async fechaModal() {
      this.dryRunResult = null
      this.manualCode = ''
      if (this.mode === 'camera' && this.canScan()) {
        await this.$nextTick()
        await this.startCamera()
      }
    }
  }
}
</script>

<style scoped>
.scan-page {
  min-height: 100vh;
  padding: clamp(16px, 4vw, 32px);
  max-width: 480px;
  margin: 0 auto;
}
.scan-header { text-align: center; margin-bottom: 24px; }
.scan-header h1 { font-size: var(--text-2xl); margin: 0 0 6px; }
.sub { color: var(--color-text-muted); font-size: var(--text-sm); margin: 0; }
.aviso {
  padding: 12px 16px;
  border-radius: var(--radius-md);
  font-size: var(--text-sm);
  margin-bottom: 16px;
}
.aviso-warn { background: #fef3c7; color: #78350f; }
.aviso-info { background: #dbeafe; color: #1e40af; }
.link { color: var(--color-accent); margin-left: 4px; }
.scanner-card { padding: 16px; display: flex; flex-direction: column; gap: 16px; }
.scanner-mode-tabs { display: flex; gap: 8px; }
.mode-tab {
  flex: 1; padding: 12px;
  background: var(--color-bg-hover);
  border: 1px solid var(--color-bg-separator);
  border-radius: var(--radius-md);
  font-size: var(--text-sm); font-weight: 600;
  cursor: pointer;
  color: var(--color-text-secondary);
  min-height: 48px;
}
.mode-tab.active {
  background: var(--color-accent);
  border-color: var(--color-accent);
  color: #fff;
}
.camera-area { display: flex; flex-direction: column; gap: 12px; }
#qr-reader {
  width: 100%;
  min-height: 280px;
  background: #000;
  border-radius: var(--radius-md);
  overflow: hidden;
}
#qr-reader :deep(video) { width: 100%; max-height: 360px; object-fit: cover; }
.manual-area { display: flex; flex-direction: column; gap: 12px; }
.label { font-size: var(--text-xs); font-weight: 600; color: var(--color-text-secondary); text-transform: uppercase; letter-spacing: 0.4px; }
.btn-block { width: 100%; padding: 14px; }

.confirm-card {
  text-align: center; max-width: 380px;
  display: flex; flex-direction: column; align-items: center; gap: 12px;
}
.conf-icon { font-size: 56px; }
.participante { font-weight: 700; font-size: var(--text-lg); margin: 0; }
.evento-info { font-size: var(--text-sm); color: var(--color-text-secondary); margin: 0; }
.evento-info small { color: var(--color-text-muted); }
.ja-info { font-size: var(--text-sm); color: var(--color-text-muted); padding: 8px 12px; background: var(--color-bg-hover); border-radius: var(--radius-sm); }
.success-bar {
  position: fixed;
  bottom: 20px; left: 50%; transform: translateX(-50%);
  background: #16a34a; color: #fff;
  padding: 14px 24px;
  border-radius: 100px;
  font-size: var(--text-sm);
  box-shadow: 0 10px 30px rgba(0,0,0,0.25);
  z-index: 200;
}
.toast-enter-active, .toast-leave-active { transition: all 0.3s ease; }
.toast-enter-from, .toast-leave-to { transform: translate(-50%, 60px); opacity: 0; }
</style>
